import { useState } from "react";
import type { TurnProgress } from "../api/contract";

type FirstLookShot = NonNullable<TurnProgress["firstLook"]>[number];

interface BlankCanvasProps {
  /** The first draft is being built: the layers settle into place one after another. */
  building: boolean;
  /** Screenshots of the first draft as it first looked, while the designer improves it. */
  firstLook?: FirstLookShot[];
}

/** "Home" for the home page, otherwise the page's last path segment in words. */
function pageName(route: string): string {
  const last = route.split("/").filter(Boolean).at(-1)?.replace(/\.html?$/i, "").replace(/[-_]+/g, " ").trim();
  return last ? `${last.charAt(0).toUpperCase()}${last.slice(1)}` : "Home";
}

/** The canvas before a site has pages. Building a first draft is shown here, where it will appear. */
export function BlankCanvas({ building, firstLook }: BlankCanvasProps) {
  const routes = [...new Set((firstLook ?? []).map((shot) => shot.route))];
  const [chosen, setChosen] = useState<string | null>(null);
  const route = chosen && routes.includes(chosen) ? chosen : routes[0];
  if (building && firstLook?.length && route) {
    const desktop = firstLook.find((shot) => shot.route === route && shot.viewport === "desktop");
    const mobile = firstLook.find((shot) => shot.route === route && shot.viewport === "mobile");
    return (
      <div className="bw-frame">
        <div className="bw-first-look" aria-busy="true">
          <p className="bw-first-look-note" role="status">
            <span className="bw-first-look-dot" aria-hidden="true" />
            A first look at your draft. Stax is polishing it now.
          </p>
          {routes.length > 1 && (
            <div className="bw-first-look-pages" role="group" aria-label="Page">
              {routes.map((option) => (
                <button key={option} type="button" aria-pressed={option === route} onClick={() => setChosen(option)}>{pageName(option)}</button>
              ))}
            </div>
          )}
          <div className="bw-first-look-shot">
            <picture>
              {mobile && <source media="(max-width: 720px)" srcSet={mobile.src} />}
              <img src={(desktop ?? mobile)!.src} alt={`First look at your ${pageName(route)} page`} />
            </picture>
          </div>
        </div>
        <span className="bw-private">Private preview</span>
      </div>
    );
  }
  return (
    <div className="bw-frame">
      <div className="bw-blank" aria-busy={building || undefined}>
        <svg className="bw-layers" data-building={building} viewBox="0 0 112 112" aria-hidden="true">
          <path d="M56 58 96 74 56 90 16 74Z" />
          <path d="M56 38 96 54 56 70 16 54Z" />
          <path d="M56 18 96 34 56 50 16 34Z" />
        </svg>
        {building ? (
          <>
            <h2>Building your first draft</h2>
            <p role="status">Your pages will appear here. Only you can see them until you publish.</p>
          </>
        ) : (
          <>
            <h2>Your website starts here</h2>
            <p>Tell Stax what you do. Your pages appear here as it builds them.</p>
          </>
        )}
      </div>
      <span className="bw-private">Private preview</span>
    </div>
  );
}
